import {
    createRouter,
    createWebHistory,
    type NavigationGuardNext,
    type RouteLocationNormalized,
    type RouteRecordRaw,
} from 'vue-router';
import axios from 'axios';
import store from '../../store';
import { analytics } from '@/lib/analytics';
import { applyRouteTitle } from '@/shared/lib/pageTitle';
import { extractAttributionFromQuery, hasAttribution, mergeAttributionIntoQuery } from '@/shared/lib/attribution';
import { adminRoutes } from '@/admin/app/router';
import { buildAuthRedirect, resolveAuthedLoginRedirect, resolveLandingRedirect, resolveMentorInfoRedirect, resolveMentorSlug, resolveMentorsCatalogAccess, resolveEventsCatalogAccess, resolvePlanningAccess, waitForScrollTarget, type AuthStoreLike, type PlanningStoreLike, type MentorSlugPayload } from './guards';
import { fetchPublicMentorBySlugApi, fetchPublicUserByIdApi } from '@/services/api.js';
import { useAdminMeStore } from '@/admin/entities/me/store';
import { startNavigation, stopNavigation } from '@/shared/lib/navigationProgress';
import { storeMentorSlugPayload } from '@/shared/lib/mentorSlugPrefetch';

// Дедуп by-slug: pre-mount гард (resolveMentorSlug) уже тянет by-slug ради
// 404-проверки — кэшируем его payload здесь, чтобы MentorPage.onMounted его
// переиспользовал, а не звал эндпоинт второй раз. Кэшируем только НЕ-redirect
// ответы: при redirect_to гард уводит на канонический slug, и MentorPage
// грузит уже его (иначе запись под старым slug осталась бы «висеть»).
const fetchAndCacheMentorBySlug = async (slug: string): Promise<MentorSlugPayload> => {
    const payload = await fetchPublicMentorBySlugApi(slug);
    if (payload && !payload.redirect_to) {
        storeMentorSlugPayload(slug, payload);
    }
    return payload;
};

// All page components are loaded dynamically via () => import(...) so the
// initial bundle stays lean. Routes that share a logical feature get the
// same webpackChunkName, which collapses sibling pages into a single chunk
// (e.g. EventInfo + EventPreview share `events`). This keeps the chunk
// count manageable instead of producing one tiny file per page.
//
// Eager kept tiny: error pages, the loading animation, and the
// ProfileDetailsSettings panel are small enough that lazy-loading them
// would add latency without saving meaningful bytes. The same is true for
// the global guards/utility imports above.
const HomePage = () => import(/* webpackChunkName: "landing" */ '../views/HomePage.vue');
const NewLandingMentor = () => import(/* webpackChunkName: "landing" */ '@/views/NewLandingMentor.vue');
const LandingMenteePage = () => import(/* webpackChunkName: "landing" */ '../views/LandingMenteePage.vue');
const SuperpeerLanding = () => import(/* webpackChunkName: "landing" */ '@/views/SuperpeerLanding.vue');

const AllEvents = () => import(/* webpackChunkName: "events" */ '@/components/pages/events/AllEvents.vue');
const EventInfo = () => import(/* webpackChunkName: "events" */ '../views/EventInfoPage.vue');
const EventPreview = () => import(/* webpackChunkName: "events" */ '@/views/EventPreview.vue');

const AllMentors = () => import(/* webpackChunkName: "mentor" */ '../views/AllMentors.vue');
const MentorPage = () => import(/* webpackChunkName: "mentor" */ '../views/MentorPage.vue');
const MyMentorsPage = () => import(/* webpackChunkName: "mentor" */ '../views/MyMentorsPage.vue');
const MyMenteePage = () => import(/* webpackChunkName: "mentor" */ '../views/MyMenteePage.vue');
const BecomeMentorForm = () => import(/* webpackChunkName: "mentor" */ '@/views/BecomeMentorForm.vue');
const ClaimMentorPage = () => import(/* webpackChunkName: "mentor" */ '../views/ClaimMentorPage.vue');

const RegisterPage = () => import(/* webpackChunkName: "auth" */ '../views/RegisterPage.vue');
const LoginPage = () => import(/* webpackChunkName: "auth" */ '../views/LoginPage.vue');
const SocialCallback = () => import(/* webpackChunkName: "auth" */ '../views/SocialCallback.vue');
const PhoneCollectionPage = () => import(/* webpackChunkName: "auth" */ '../views/PhoneCollectionPage.vue');

// Meeting/conference views import the heavy Jazz/Zoom SDKs transitively.
// Grouping them together keeps the SDK confined to the `meeting` chunk so
// it never weighs down the landing/dashboard first paint.
const MeetingPage = () => import(/* webpackChunkName: "meeting" */ '@/views/MeetingPage.vue');
const ZoomCallPage = () => import(/* webpackChunkName: "meeting" */ '../views/ZoomCallPage.vue');
const ConferenceRecordingPage = () => import(/* webpackChunkName: "meeting" */ '../views/ConferenceRecordingPage.vue');

const CalendarPage = () => import(/* webpackChunkName: "calendar" */ '@/views/CalendarPage.vue');

const ChatsPage = () => import(/* webpackChunkName: "chats" */ '@/views/ChatsPage.vue');

const ProfileSettings = () => import(/* webpackChunkName: "settings" */ '@/views/ProfileSettings.vue');
const Profile = () => import(/* webpackChunkName: "settings" */ '@/components/pages/settings/ProfileDetailsSettings.vue');
// /settings/availability is kept as a legacy redirect so old emails,
// bookmarks and onboarding links land in the calendar instead of resurrecting
// the removed schedule tab in ProfileSettings.

const CreateEntityPage = () => import(/* webpackChunkName: "create" */ '@/views/CreateEntityPage.vue');
const Statistics = () => import(/* webpackChunkName: "create" */ '@/views/Statistics.vue');

const ConsultationPage = () => import(/* webpackChunkName: "consultation" */ '../views/ConsultationPage.vue');
const SessionHubPage = () => import(/* webpackChunkName: "session" */ '../views/SessionHubPage.vue');

const LegalDocumentPage = () => import(/* webpackChunkName: "legal" */ '@/views/LegalDocumentPage.vue');

// Error/404 pages and the loading animation stay tiny but they're still
// only needed off the happy path, so we lazy-load them in a shared
// `errors` chunk. Keeps router/index.ts free of large static imports.
const LandingLoadingAnimation = () => import(/* webpackChunkName: "errors" */ '@/components/Animations/LandingLoadingAnimation.vue');
const NotFoundError = () => import(/* webpackChunkName: "errors" */ '@/pages/errors/NotFoundError.vue');
const ForbiddenError = () => import(/* webpackChunkName: "errors" */ '@/pages/errors/ForbiddenError.vue');
const UnauthorizedError = () => import(/* webpackChunkName: "errors" */ '@/pages/errors/UnauthorizedError.vue');
const InternalServerError = () => import(/* webpackChunkName: "errors" */ '@/pages/errors/InternalServerError.vue');

const HIDE_APP_DOCK = { hideAppDock: true } as const;
const HIDE_APP_CHROME = { hideAppChrome: true } as const;

// founder-call is gated server-side only:
//   1) settings.FOUNDER_CALL_ENABLED disables the BE token endpoint (404)
//   2) invite JWT verification rejects bad/expired links
// A duplicate build-time FE flag would add no security (BE returns 404
// when disabled anyway) but risks silent breakage if the env var goes
// missing in a prod build.  Routes are always registered; an instance
// with FOUNDER_CALL_ENABLED=False just shows the landing and gets a
// clear 404 from BE on submit.
const legacyBaseMeetEnabled =
    String((globalThis as { process?: { env?: Record<string, string | undefined> } }).process?.env?.VUE_APP_ENABLE_LEGACY_BASE_MEET ?? '').toLowerCase() === 'true';
// Vue CLI / webpack DefinePlugin подменяет `process.env.VUE_APP_*` на литерал
// строки при сборке. Доступ ДОЛЖЕН быть прямым (без globalThis-cast), иначе
// webpack не распознает паттерн и в runtime `process` будет undefined.
const supportAdminEnabled =
    String(process.env.VUE_APP_SUPPORT_ADMIN_ENABLED ?? '').toLowerCase() === 'true';
const supportUserEnabled =
    String(process.env.VUE_APP_SUPPORT_REPORT_BUTTON_ENABLED ?? '').toLowerCase() === 'true';

// Support user routes (PR-12) — gated behind VUE_APP_SUPPORT_REPORT_BUTTON_ENABLED.
// Same flag controls both the routes and the in-page <ReportProblemButton>
// component, so flipping the flag off hides the entire user surface area.
// Lazy-loaded so the support bundle is split off from the main app.
const supportUserRoutes: RouteRecordRaw[] = supportUserEnabled
    ? [
          {
              path: '/support',
              name: 'support-my-tickets',
              component: () => import('@/views/SupportPage.vue'),
              meta: { requiresAuth: true, pageTitle: 'Мои обращения' },
          },
          {
              path: '/support/:publicId',
              name: 'support-ticket',
              component: () => import('@/views/SupportTicketPage.vue'),
              props: true,
              meta: { requiresAuth: true, pageTitle: 'Обращение в поддержку' },
          },
      ]
    : [];

const routes: RouteRecordRaw[] = [
    { path: '/Home', component: HomePage, meta: { header: 'Landing', pageTitle: 'Главная', redirectAuthedToDashboard: true, ...HIDE_APP_DOCK } },
    // 2026-05-23: redirect only EXISTING mentors away from this page.
    // Non-mentor authed users (typical entry point: «Узнать подробнее» on
    // /forbidden when a menti opens /Statistics) must be able to read the
    // landing before deciding whether to apply for the role.
    { path: '/NewLandingMentor', component: NewLandingMentor, meta: { header: 'Landing', pageTitle: 'Лендинг для наставников', redirectMentorsToDashboard: true, ...HIDE_APP_DOCK } },
    { path: '/Statistics', component: Statistics, meta: { header: 'Search', requiresAuth: true, requiredRole: 'mentor', pageTitle: 'Статистика' } },
    // Каталог групповых сессий на запуске закрыт так же, как каталог
    // наставников ниже — admin-only (решение владельца 13.08.2026). Страницы
    // самих событий по прямой ссылке остаются публичными: наставник делится
    // ссылкой, гейт видимости решает offering_visibility на бэкенде.
    { path: '/AllEvents', component: AllEvents, beforeEnter: () => resolveEventsCatalogAccess(useAdminMeStore()), meta: { header: 'Search', pageTitle: 'Все сессии' } },
    // Каталог наставников пока не несёт смысла для обычных пользователей →
    // временно доступен только админам (см. resolveMentorsCatalogAccess).
    // useAdminMeStore() вызывается внутри guard'а (в момент навигации Pinia
    // уже активна), стор кэширует ответ /admin-api/me/.
    { path: '/AllMentors', component: AllMentors, beforeEnter: () => resolveMentorsCatalogAccess(useAdminMeStore()), meta: { header: 'Search', pageTitle: 'Все наставники' } },
    { path: '/SuperpeerLanding', component: SuperpeerLanding, meta: { header: 'Search', pageTitle: 'Superpeer', ...HIDE_APP_DOCK } },
    { path: '/', component: () => import('@/views/CreatorLandingTest.vue'), meta: { pageTitle: 'Платные созвоны и личные сессии', redirectAuthedToDashboard: true, ...HIDE_APP_CHROME } },
    // 2026-05-23: dropped HIDE_APP_DOCK on these 4 public-detail pages.
    // Logged-in users navigating from catalog/calendar need the bottom nav
    // to get back home. Anonymous visitors still see no dock — App.vue
    // auto-hides it when `!isAuthenticated`.
    { path: '/event_info/:id/', name: 'EventInfo', component: EventInfo, meta: { header: 'Search', pageTitle: 'Сессия' } },
    { path: '/event_info/preview', name: 'EventPreview', component: EventPreview, meta: { header: 'Search', pageTitle: 'Превью сессии' } },
    { path: '/consultation/:id', alias: '/consultation/:id/', name: 'ConsultationPublic', component: ConsultationPage, meta: { header: 'Search', pageTitle: 'Личная сессия' } },
    {
        // E50: рендер по id остаётся только для профилей без слага. Если слаг
        // есть — уводим на канонический /<slug>, иначе два адреса показывают
        // разные данные (см. resolveMentorInfoRedirect). Роут не удаляем: это
        // штатный фолбэк и живая внешняя поверхность (OG-вьюхи, CTA
        // уведомлений, claim-страница, «Мои наставники», календарь).
        path: '/mentor_info/:mentorId',
        name: 'MentorInfo',
        component: MentorPage,
        beforeEnter: (to) => resolveMentorInfoRedirect(to, fetchPublicUserByIdApi),
        meta: { header: 'Search', pageTitle: 'Профиль наставника' },
    },
    {
        path: '/event-conference/:event_id',
        name: 'EventConference',
        component: MeetingPage,
        meta: { requiresAuth: true, pageTitle: 'Трансляция сессии', ...HIDE_APP_CHROME }
    },
    { path: '/MeetingPage', name: 'MeetingPage', component: MeetingPage, meta: { requiresAuth: false, pageTitle: 'Видеоконференция', ...HIDE_APP_CHROME } },
    {
        path: '/BaseMeet',
        name: 'BaseMeet',
        component: legacyBaseMeetEnabled ? () => import('@/_legacy/views/BaseMeet.vue') : NotFoundError,
        meta: legacyBaseMeetEnabled
            ? { requiresAuth: true, pageTitle: 'Комната сессии', ...HIDE_APP_CHROME }
            : { pageTitle: 'Страница не найдена', ...HIDE_APP_DOCK },
    },

    // Маршрут для личных сессий (V2: параметр несёт OrderItem.public_id —
    // orderItemId; MeetingPage/токен-эндпоинт резолвят OrderItem →
    // ServiceInstance → Conference).
    {
        path: '/booking-conference/:orderItemId',
        name: 'BookingConference',
        component: MeetingPage,
        meta: { requiresAuth: true, pageTitle: 'Сессия по бронированию', ...HIDE_APP_CHROME }
    },

    // Альтернативный вариант с query параметрами
    { path: '/conference', name: 'Conference', component: MeetingPage, meta: { requiresAuth: true, pageTitle: 'Видеоконференция', ...HIDE_APP_CHROME } },

    { path: '/register', component: RegisterPage, meta: { header: 'Search', pageTitle: 'Регистрация', ...HIDE_APP_DOCK } },
    { path: '/login', component: LoginPage, meta: { header: 'Search', pageTitle: 'Вход', ...HIDE_APP_DOCK } },
    // Social auth: backend redirects to /auth/callback after JWT cookies are set.
    // The page calls fetchUser and routes onwards (phone collection or dashboard).
    { path: '/auth/callback', name: 'SocialCallback', component: SocialCallback, meta: { pageTitle: 'Подтверждаем вход', ...HIDE_APP_CHROME } },
    // Post-signup phone collection — forced step for OAuth-only users.
    // `requiresPhoneCollection: true` lets the global guard understand that
    // bypassing this page (e.g. browser back button) must redirect back to it.
    { path: '/complete-profile/phone', name: 'CompleteProfilePhone', component: PhoneCollectionPage, meta: { requiresAuth: true, requiresPhoneCollection: true, pageTitle: 'Укажите номер телефона', ...HIDE_APP_CHROME } },
    {
        path: '/legal/privacy-policy',
        name: 'PrivacyPolicy',
        component: LegalDocumentPage,
        props: { documentType: 'privacy-policy' },
        meta: { header: 'Landing', pageTitle: 'Политика конфиденциальности', ...HIDE_APP_DOCK }
    },
    {
        path: '/legal/public-offer',
        name: 'PublicOffer',
        component: LegalDocumentPage,
        props: { documentType: 'public-offer' },
        meta: { header: 'Landing', pageTitle: 'Публичная оферта', ...HIDE_APP_DOCK }
    },
    {
        path: '/legal/secure-deal-offer',
        name: 'SecureDealOffer',
        component: LegalDocumentPage,
        props: { documentType: 'secure-deal-offer' },
        meta: { header: 'Landing', pageTitle: 'Публичная оферта безопасной сделки', ...HIDE_APP_DOCK }
    },
    // E7 stopgap (см. docs/2026-05-26-complaint-anchor-backlog.md):
    // публичная страница условий возврата, на которую ссылается
    // tooltip-hint «Все условия →» с кнопки «Открыть спор» в
    // `EventCard.vue`. Стаб; в P2 заменим на CMS-контент.
    {
        path: '/refund-policy',
        name: 'RefundPolicy',
        component: () => import('@/views/RefundPolicyPage.vue'),
        meta: { header: 'Landing', pageTitle: 'Политика возврата', ...HIDE_APP_DOCK },
    },

    { path: '/ProfileSettings', component: ProfileSettings, meta: { header: 'Search', pageTitle: 'Настройки профиля' } },
    { path: '/Profile', component: Profile, meta: { header: 'Search', pageTitle: 'Профиль' } },
    {
        // Legacy alias — schedule management now lives in the calendar,
        // not as a ProfileSettings tab.
        path: '/settings/availability',
        name: 'MentorAvailabilitySettings',
        redirect: { path: '/CalendarPage' },
        meta: {
            requiresAuth: true,
            pageTitle: 'Календарь',
        },
    },
    { path: '/dashboard', component: () => import('@/views/DashboardPage.vue'), meta: { header: 'Search', requiresAuth: true, pageTitle: 'Главная' } },

    // Раздел планирования. Баг 3.2: доступ определяется не сырой ролью
    // (она становится `mentor` лишь при одобрении заявки), а
    // `selling_readiness` — наставник «на проверке» тоже сюда допускается
    // (см. resolvePlanningAccess / backend resolve_next_step).
    {
        path: '/CreateEntityPage',
        component: CreateEntityPage,
        meta: { header: 'Search', requiresAuth: true, requiresPlanningAccess: true, pageTitle: 'Планирование событий' }
    },

    { path: '/preflight', name: 'preflight', component: () => import('@/views/PreflightPage.vue'), meta: { header: 'Default', pageTitle: 'Проверка связи', hideAppDock: true } },
    { path: '/CalendarPage', component: CalendarPage, meta: { header: 'Search', requiresAuth: true, pageTitle: 'Календарь' } },
    { path: '/CalendarPage/history', component: () => import(/* webpackChunkName: "calendar-history" */ '@/views/CalendarHistoryPage.vue'), meta: { header: 'Search', requiresAuth: true, pageTitle: 'История' } },
    { path: '/ChatsPage', component: ChatsPage, meta: { header: 'Search', requiresAuth: true, pageTitle: 'Чаты' } },
    { path: '/MyMentorsPage', component: MyMentorsPage, meta: { requiresAuth: true, pageTitle: 'Мои наставники' } },
    { path: '/MyMentee', component: MyMenteePage, meta: { requiresAuth: true, requiredRole: 'mentor', pageTitle: 'Мои ученики' } },
    { path: '/LandingMenteePage', component: LandingMenteePage, meta: { header: 'Landing', pageTitle: 'Лендинг для учеников', redirectAuthedToDashboard: true, ...HIDE_APP_DOCK } },
    { path: '/loads', component: LandingLoadingAnimation, meta: { pageTitle: 'Загрузка', ...HIDE_APP_CHROME } },
    {
        path: '/creator-test',
        name: 'CreatorLandingTest',
        component: () => import('@/views/CreatorLandingTest.vue'),
        meta: {
            pageTitle: 'Платные созвоны и личные сессии',
            redirectAuthedToDashboard: true,
            ...HIDE_APP_CHROME,
        },
    },
    { path: '/api/mentor-claims/preview', name: 'MentorClaimLegacyPreview', component: ClaimMentorPage, meta: { header: 'Landing', pageTitle: 'Привязка аккаунта', ...HIDE_APP_DOCK } },
    { path: '/api/mentor-claims/preview/', name: 'MentorClaimLegacyPreviewSlash', component: ClaimMentorPage, meta: { header: 'Landing', pageTitle: 'Привязка аккаунта', ...HIDE_APP_DOCK } },
    { path: '/mentor-claim/:token?', name: 'MentorClaim', component: ClaimMentorPage, meta: { header: 'Landing', pageTitle: 'Привязка аккаунта', ...HIDE_APP_DOCK } },
    {
        path: '/payment-return',
        name: 'PaymentReturn',
        component: () => import('@/views/PaymentReturn.vue'),
        meta: { header: 'Search', pageTitle: 'Подтверждение оплаты', ...HIDE_APP_DOCK },
    },
    // V2 hard cutover: the canonical session id is now OrderItem.public_id
    // (orderItemId), not the deleted ConsultationBooking.public_id. The
    // component reads route.params.orderItemId.
    { path: '/session/:orderItemId', name: 'SessionHub', component: SessionHubPage, meta: { requiresAuth: true, header: 'Search', pageTitle: 'Сессия' } },
    // Legacy catcher: any old /session deep-link (emails, bookmarks, stale
    // notifications carrying a removed ConsultationBooking public_id) can no
    // longer resolve — the booking table is gone. Bounce it to the calendar
    // where the user can re-open the live session card. The param value is
    // discarded (pure redirect). Per the v2 cutover blueprint
    // ("redirect old → /CalendarPage").
    { path: '/session-legacy/:legacyId', name: 'SessionHubLegacy', redirect: { path: '/CalendarPage' } },
    {
        path: '/mentor-application/submitted',
        name: 'MentorApplicationSubmitted',
        component: () => import(/* webpackChunkName: "become-mentor" */ '@/views/MentorApplicationSubmittedPage.vue'),
        meta: { requiresAuth: true, pageTitle: 'Заявка отправлена' },
    },
    {
        // Bug 4.2: this single-segment matcher swallows ANY one-word URL before
        // the `/:pathMatch(.*)*` not-found route below. Resolve the slug in a
        // pre-mount guard so a typo'd/stale link lands on the honest 404 (no
        // «Профиль наставника» chrome flash) instead of mounting MentorPage only to
        // swap it out after the backend 404. Valid profiles are untouched.
        path: '/:slug',
        name: 'MentorPublic',
        component: MentorPage,
        beforeEnter: (to) => resolveMentorSlug(to, fetchAndCacheMentorBySlug),
        meta: { header: 'Landing', pageTitle: 'Профиль наставника' },
    },
    // 2026-05-23: error pages no longer hide the dock for logged-in users —
    // a menti who hits /forbidden trying to open /Statistics still needs
    // a way back. App.vue auto-hides the dock for anonymous visitors so
    // these still render dockless for guests.
    { path: '/:pathMatch(.*)*', name: 'not-found', component: NotFoundError, meta: { pageTitle: 'Страница не найдена' } },
    { path: '/forbidden', name: 'forbidden', component: ForbiddenError, meta: { pageTitle: 'Доступ запрещен' } },
    { path: '/unauthorized', name: 'unauthorized', component: UnauthorizedError, meta: { pageTitle: 'Неавторизован' } },
    { path: '/internal-server-error', name: 'internal-server-error', component: InternalServerError, meta: { pageTitle: 'Ошибка сервера' } },
    { path: '/become-mentor', name: 'BecomeMentor', component: BecomeMentorForm, meta: { requiresAuth: true, pageTitle: 'Стать наставником' } },
    { path: '/notifications', component: () => import('../views/NotificationsPage.vue'), meta: { requiresAuth: true, pageTitle: 'Уведомления' } },
    /* eslint-disable */
    {
        path: '/events/:eventId/call',
        name: 'call-event',
        component: ZoomCallPage,
        props: (route: RouteLocationNormalized) => ({
            initialContext: 'events',
            initialEntityId: String(route.params.eventId ?? '')
        }),
        meta: { requiresAuth: true, contextType: 'events', pageTitle: 'Онлайн-событие', ...HIDE_APP_CHROME },
    },
    {
        path: '/open-events/:eventId/live',
        name: 'open-call-event',
        component: ZoomCallPage,
        props: (route: RouteLocationNormalized) => ({
            initialContext: 'events',
            initialEntityId: String(route.params.eventId ?? ''),
        }),
        meta: { contextType: 'events', requiresAuth: true, pageTitle: 'Открытая трансляция', ...HIDE_APP_CHROME },
    },
    {
        // V2 cutover: the URL param carries an OrderItem.public_id (orderItemId),
        // not a ConsultationBooking id. ZoomCallPage already sniffs
        // ``route.params.orderItemId``. The contextType string
        // 'consultationbooking' is INTENTIONALLY preserved — it is a backend
        // contract for the Zoom-token endpoint, which resolves
        // OrderItem.public_id → ServiceInstance → Conference. Do not rename the
        // context string without a coordinated BE change.
        path: '/consultations/:orderItemId/call',
        name: 'call-consultation',
        component: ZoomCallPage,
        props: (route: RouteLocationNormalized) => ({
            initialContext: 'consultationbooking',
            initialEntityId: String(route.params.orderItemId ?? '')
        }),
        meta: { requiresAuth: true, contextType: 'consultationbooking', pageTitle: 'Личная сессия в эфире', ...HIDE_APP_CHROME },
    },
    {
        path: '/events/:eventId/recording',
        name: 'recording-event',
        component: ConferenceRecordingPage,
        props: (route: RouteLocationNormalized) => ({
            initialContext: 'events',
            initialEntityId: String(route.params.eventId ?? '')
        }),
        meta: { requiresAuth: true, contextType: 'events', pageTitle: 'Запись сессии', ...HIDE_APP_CHROME },
    },
    {
        // V2 cutover: the param carries an OrderItem.public_id (orderItemId).
        // ConferenceRecordingPage already sniffs ``route.params.orderItemId``.
        // The contextType string is kept for the same reasons as the
        // call-consultation route above (BE token contract resolves
        // OrderItem → ServiceInstance → Conference for the recording).
        path: '/consultations/:orderItemId/recording',
        name: 'recording-consultation',
        component: ConferenceRecordingPage,
        props: (route: RouteLocationNormalized) => ({
            initialContext: 'consultationbooking',
            initialEntityId: String(route.params.orderItemId ?? '')
        }),
        meta: { requiresAuth: true, contextType: 'consultationbooking', pageTitle: 'Запись личной сессии', ...HIDE_APP_CHROME },
    },
];

if (adminRoutes.length) {
    // Inject before the catch-all `/:slug` so paths like `/admin` and
    // `/admin/dashboard` are not swallowed by the public mentor profile route.
    const slugRouteIndex = routes.findIndex((route) => route.path === '/:slug');
    routes.splice(
        slugRouteIndex >= 0 ? slugRouteIndex : routes.length,
        0,
        ...adminRoutes,
    );
}

if (supportUserRoutes.length) {
    // Same splice-before-catch-all pattern as the admin routes — `/support`
    // and `/support/:publicId` must beat the `/:slug` mentor profile match.
    const slugRouteIndex = routes.findIndex((route) => route.path === '/:slug');
    routes.splice(
        slugRouteIndex >= 0 ? slugRouteIndex : routes.length,
        0,
        ...supportUserRoutes,
    );
}

// Founder-call landing + live-call routes (always registered — gating is
// server-side only, see comment near `legacyBaseMeetEnabled` declaration).
// Inject before /:slug catch-all so the URL doesn't get treated as a
// mentor profile slug (which would 404 with «Mentor not found»).
{
    const mentorClaimRouteIndex = routes.findIndex((route) => route.name === 'MentorClaimLegacyPreview');
    const consultationZoomRouteIndex = routes.findIndex((route) => route.path === '/consultations/:orderItemId/call');
    routes.splice(
        mentorClaimRouteIndex >= 0 ? mentorClaimRouteIndex : routes.length,
        0,
        {
            path: '/founder-call',
            name: 'FounderCallLanding',
            component: () => import('../views/FounderCallLanding.vue'),
            meta: { header: 'Landing', pageTitle: 'Созвон с основателем', ...HIDE_APP_DOCK },
        },
    );
    routes.splice(
        consultationZoomRouteIndex >= 0 ? consultationZoomRouteIndex : routes.length,
        0,
        {
            path: '/founder-call/live/:eventId?',
            name: 'founder-call',
            component: () => import('../views/FounderZoomPage.vue'),
            // Пропсов у FounderZoomPage нет: она читает eventId и invite прямо
            // из route. Прежние initialContext/initialEntityId/allowGuest
            // компонент не объявлял — они утекали атрибутами в DOM, а
            // `allowGuest` выглядел как контракт доступа, которого не было.
            meta: {
                contextType: 'events',
                publicAccess: true,
                requiresAuth: false,
                pageTitle: 'Прямая трансляция с основателем',
                ...HIDE_APP_CHROME,
            },
        },
    );
}

const CLAIM_TAB_SCROLL_PRESERVE_ROUTE_NAMES = new Set([
    'MentorClaim',
    'MentorClaimLegacyPreview',
    'MentorClaimLegacyPreviewSlash',
]);

const router = createRouter({
    history: createWebHistory('/'),
    routes,
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            const targetTop = savedPosition.top ?? 0;
            // E41: возврат «Назад» на страницу, которая грузит данные в
            // onMounted (MentorPage и др.). Синхронный savedPosition браузер
            // обрезал бы — документ ещё пустой. Ждём, пока он дорастёт до
            // сохранённой позиции; таймаут внутри хелпера не даёт зависнуть.
            if (targetTop > 0 && typeof window !== 'undefined') {
                return waitForScrollTarget(targetTop, {
                    getDocumentHeight: () => document.documentElement.scrollHeight,
                    getViewportHeight: () => window.innerHeight,
                    schedule: (callback) => window.requestAnimationFrame(callback),
                    now: () => Date.now(),
                }).then(() => savedPosition);
            }
            return savedPosition;
        }

        const toRouteName = String(to.name || '');
        const fromRouteName = String(from.name || '');
        const isClaimTabSwitch =
            to.path === from.path &&
            CLAIM_TAB_SCROLL_PRESERVE_ROUTE_NAMES.has(toRouteName) &&
            CLAIM_TAB_SCROLL_PRESERVE_ROUTE_NAMES.has(fromRouteName) &&
            to.query.tab !== from.query.tab;

        if (isClaimTabSwitch) {
            return false;
        }

        if (to.hash) {
            const prefersReducedMotion =
                typeof window !== 'undefined' &&
                window.matchMedia('(prefers-reduced-motion: reduce)').matches;

            const rawHash = to.hash.startsWith('#') ? to.hash.slice(1) : to.hash;
            const decodedHash = decodeURIComponent(rawHash || '');

            if (decodedHash && typeof document !== 'undefined') {
                const targetById = document.getElementById(decodedHash);

                if (targetById) {
                    return {
                        el: targetById,
                        top: 10,
                        behavior: prefersReducedMotion ? 'auto' : 'smooth',
                    };
                }

                if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
                    const escapedSelector = `#${CSS.escape(decodedHash)}`;
                    const targetBySelector = document.querySelector(escapedSelector);

                    if (targetBySelector) {
                        return {
                            el: targetBySelector,
                            top: 10,
                            behavior: prefersReducedMotion ? 'auto' : 'smooth',
                        };
                    }
                }
            }
        }

        return { top: 0 };
    }
});

router.afterEach((to: RouteLocationNormalized) => {
    // Навигация завершена — гасим верхнюю полосу-индикатор.
    stopNavigation();
    applyRouteTitle(to);
    try {
        const fullPath = router.resolve(to).href || to.fullPath;
        analytics.trackPageView(fullPath);
    } catch (err) {
        console.warn('Analytics page view error', err);
    }
});

// Навигация упала (отменённый гард / ошибка загрузки chunk'а) — тоже гасим
// полосу, иначе индикатор «завис» бы после неудачного перехода.
router.onError(() => {
    stopNavigation();
});

router.beforeEach(async (to: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext) => {
    // Первой строкой, синхронно — до любых await (fetchUser и т.п.): полоса
    // должна появиться СРАЗУ на клик, а не после того, как гард догрузит юзера.
    // Единственный beforeEach в приложении, поэтому здесь — самое раннее место.
    startNavigation();

    const toMarkers = extractAttributionFromQuery(to.query);
    const fromMarkers = extractAttributionFromQuery(_from.query);

    if (!hasAttribution(toMarkers) && hasAttribution(fromMarkers)) {
        next({
            path: to.path,
            query: mergeAttributionIntoQuery(to.query, fromMarkers),
            hash: to.hash,
            replace: true,
        });
        return;
    }

    // Trap-prevention: authenticated users must never land on the marketing
    // splash. The landing has no clear "back to app" escape — clicking the
    // header logo or typing the URL directly should silently route them to
    // /dashboard. See resolveLandingRedirect for cold-start handling.
    const landingRedirect = await resolveLandingRedirect(to, store as unknown as AuthStoreLike);
    if (landingRedirect) {
        next(landingRedirect);
        return;
    }

    const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
    const requiresRole = to.meta.requiredRole as string | undefined;
    const requiresRegistration = to.meta.requiresRegistration as boolean | undefined;
    // Баг 3.2 — раздел планирования: доступ по selling_readiness, не по роли.
    const requiresPlanningAccess = Boolean(to.meta.requiresPlanningAccess);

    const isAuthenticated = store.getters.isAuthenticated;
    const userRole = store.getters.userRole; // теперь приходит из user.role

    // Pages users can visit even without finishing phone collection:
    //   - the phone collection page itself (otherwise infinite loop)
    //   - the OAuth callback (it routes us TO phone collection)
    //   - login/register/legal pages (allow logging out / reviewing terms)
    const PHONE_COLLECTION_EXEMPT_PATHS = new Set([
        '/complete-profile/phone',
        '/auth/callback',
        '/login',
        '/register',
        '/forbidden',
        '/unauthorized',
        '/internal-server-error',
    ]);
    const isPhoneCollectionExempt = (path: string): boolean => {
        if (PHONE_COLLECTION_EXEMPT_PATHS.has(path)) return true;
        return path.startsWith('/legal/');
    };

    const enforcePhoneCollection = (): boolean => {
        if (!store.getters.needsPhoneCollection) return false;
        if (isPhoneCollectionExempt(to.path)) return false;
        return true;
    };

    // Не редиректим на /login для страниц регистрации и логина
    const isAuthPage = to.path === '/login' || to.path === '/register';
    // Issue #1 — buildAuthRedirect now branches on /become-mentor → /register
    // (with intent=mentor preserved through the redirect) instead of
    // unconditionally landing the anonymous user on /login, which is a dead
    // end for a brand-new visitor coming off the creator-landing CTA.
    const buildLoginRedirect = () => {
        const activeMarkers = hasAttribution(toMarkers) ? toMarkers : fromMarkers;
        const target = buildAuthRedirect(to.fullPath);
        return {
            path: target.path,
            query: mergeAttributionIntoQuery(target.query, activeMarkers),
        };
    };

    // Финальный пропуск аутентифицированного пользователя. Баг 3.2: для
    // раздела планирования доступ решает selling_readiness (а не роль), с
    // реактуализацией стейл-стора — поэтому проверка живёт здесь, в общей
    // точке для тёплого и cold-start authed путей.
    const proceedAuthed = async (): Promise<void> => {
        if (requiresPlanningAccess) {
            const planning = await resolvePlanningAccess(store as unknown as PlanningStoreLike);
            if (planning !== true) {
                next(planning);
                return;
            }
        }
        next();
    };

    if (requiresAuth && !isAuthenticated) {
        if (!store.state.userChecked) {
            try {
                await store.dispatch('fetchUser');
                store.commit('setUserChecked', true); // теперь есть такая мутация
                if (store.getters.isAuthenticated) {
                    await proceedAuthed();
                } else {
                    // Не редиректим, если уже на странице регистрации
                    if (to.path === '/register') {
                        next();
                    } else {
                        next(buildLoginRedirect());
                    }
                }
            } catch (error) {
                // Не редиректим, если уже на странице регистрации
                if (to.path === '/register') {
                    next();
                } else {
                    next(buildLoginRedirect());
                }
            }
        } else {
            // Не редиректим, если уже на странице регистрации
            if (to.path === '/register') {
                next();
            } else {
                next(buildLoginRedirect());
            }
        }
    } else if (requiresRole && userRole !== requiresRole) {
        next('/forbidden');
    } else if (isAuthenticated && to.path === '/login') {
        // Приоритет ?redirect= → страница-источник → /dashboard, плюс
        // hardening против протокол-относительных URL. Вся логика — в
        // resolveAuthedLoginRedirect (тестируется без монтирования роутера).
        next(resolveAuthedLoginRedirect(to, _from));
    } else {
        // Progressive registration: phone is no longer collected globally.
        // The booking/payment flow enforces phone collection at the payment step.
        // proceedAuthed применяет планирование-гейт (баг 3.2) для authed-юзера;
        // для всех прочих маршрутов он сводится к обычному next().
        await proceedAuthed();
    }
});

// Примерная функция проверки регистрации (оставлена как в исходнике)
async function checkRegistration(meetingId: string) {
    try {
        const response = await axios.get(`/meetings/${meetingId}/is_registered/`);
        return response.data.isRegistered;
    } catch (error) {
        console.error('Ошибка проверки регистрации:', error);
        return false;
    }
}

export default router
